import JSONAPISerializer from '@ember-data/serializer/json-api';

import { isPresent } from '@ember/utils';
import formatRelationshipsLinks from 'garaje/utils/relationships-formatter';
import embeddedRelationship from 'garaje/utils/embedded-relationship';
import config from 'garaje/config/environment';
import { getStatusCode } from 'garaje/utils/parse-error';

export default class extends JSONAPISerializer {
  normalizeResponse(store, typeClass, payload) {
    embeddedRelationship(payload, typeClass);
    const normalized = super.normalizeResponse(...arguments);

    // Ember Data 5.3 bug: _normalizeDocumentHelper doesn't filter null entries
    // from the data array (unlike included, where nulls are filtered). Null entries
    // occur when _normalizeResourceHelper encounters an unknown model type.
    // These null entries crash validateResourceFields and cacheUpsert.
    if (Array.isArray(normalized.data)) {
      const originalLength = normalized.data.length;
      normalized.data = normalized.data.filter(Boolean);
      if (!config.isProduction && normalized.data.length !== originalLength) {
        // eslint-disable-next-line no-console
        console.warn(
          `[ApplicationSerializer] Filtered ${originalLength - normalized.data.length} null entries from data array (unknown model types in API response)`,
        );
      }
    }

    // Don't let a sparse `device` sideload erase telemetry attributes we already hold.
    // Under `devices-service-status-api` the iPad list is backed by `device` records
    // populated from devices-service telemetry. A `printer` query sideloads the same
    // device by id but serializes it sparsely (e.g. `device-type: null`); pushing that
    // would null out `deviceType`, flip `device.isIpad` to false, and drop the iPad from
    // the list. A resource that arrives only in `included` is supplementary, so for a
    // `device` we already have, ignore the null attributes it carries and keep our
    // values. Present (non-null) attributes still update as usual.
    if (Array.isArray(normalized.included)) {
      for (const resource of normalized.included) {
        if (resource?.type !== 'device' || !resource.attributes) continue;
        if (!store.peekRecord('device', resource.id)) continue;
        for (const key of Object.keys(resource.attributes)) {
          if (resource.attributes[key] === null) {
            delete resource.attributes[key];
          }
        }
      }
    }

    return normalized;
  }

  normalize(typeClass, hash) {
    let { relationships } = hash;
    if (isPresent(relationships)) {
      relationships = formatRelationshipsLinks(relationships);
    }

    return super.normalize(...arguments);
  }

  extractErrors(store, typeClass, payload) {
    const code = Number(getStatusCode(payload));

    payload.errors?.forEach((error) => {
      // handle missing error source as ember data internals now expects them
      if (!error.source) error.source = { pointer: '/data' };
      error.meta = { status: error.status };
    });

    // add missing status code now stripped from ErrorClass by ember data internals
    if (!Number.isNaN(code)) payload.code = code;

    return super.extractErrors(...arguments);
  }
}
